import React, { useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
} from "@heroicons/react/20/solid";
import toast from "react-hot-toast";
import * as XLSX from "xlsx"; 
import { RootDispatch, RootState } from "../../store";
import { fetchMonthlyLossReport } from "../../store/slices/shrinkSlice";

interface LossReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LossReportModal: React.FC<LossReportModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const { loading } = useSelector((state: RootState) => state.shrink);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [formData, setFormData] = useState({
    startMonth: currentMonth,
    startYear: currentYear,
    endMonth: currentMonth,
    endYear: currentYear,
  });

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Restrict to current and past years only
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const triggerDownload = (reportData: any[], format: "csv" | "xlsx") => {
    if (!reportData || reportData.length === 0) {
      toast.error("No data found for this range");
      return;
    }

    const fileName = `Loss_Report_${formData.startMonth}_${formData.startYear}_to_${formData.endMonth}_${formData.endYear}`;

    if (format === "csv") {
      const headers = Object.keys(reportData[0]).join(",");
      const rows = reportData.map((row: any) =>
        Object.values(row)
          .map((value) => `"${value}"`)
          .join(","),
      );
      const csvContent = [headers, ...rows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${fileName}.csv`);
      link.click();
    } else {
      const worksheet = XLSX.utils.json_to_sheet(reportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Loss Report");
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
    }

    toast.success(`${format.toUpperCase()} downloaded successfully`);
    onClose();
  };

  const handleExport = async (format: "csv" | "xlsx") => {
    try {
      const result = await dispatch(
        fetchMonthlyLossReport({ ...formData, format }),
      ).unwrap();
      triggerDownload(result, format);
    } catch (error: any) {
      toast.error(error || "Failed to generate report");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-200">
        <div className="bg-gray-50 px-6 py-5 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
              <CalendarIcon className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-bold text-gray-800">
              Monthly Loss Report
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors text-xl"
          >
            ✕
          </button>
        </div>

        <div className="p-8 space-y-8">
          {/* Start Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Starting From
              </label>
              <div className="flex gap-2">
                <select
                  value={formData.startMonth}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      startMonth: parseInt(e.target.value),
                    })
                  }
                  className="flex-1 p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {months.map((m, i) => (
                    <option key={m} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  value={formData.startYear}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      startYear: parseInt(e.target.value),
                    })
                  }
                  className="flex-1 p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* End Selection */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Ending At
              </label>
              <div className="flex gap-2">
                <select
                  value={formData.endMonth}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      endMonth: parseInt(e.target.value),
                    })
                  }
                  className="flex-1 p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {months.map((m, i) => (
                    <option key={m} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  value={formData.endYear}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      endYear: parseInt(e.target.value),
                    })
                  }
                  className="flex-1 p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 px-8 py-5 flex justify-end items-center gap-4">
          <button
            onClick={onClose}
            className="text-sm font-semibold text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>

          <Menu as="div" className="relative inline-block text-left">
            <MenuButton
              disabled={loading}
              className={`inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-bold shadow-md transition-all ${
                loading
                  ? "bg-gray-300 text-gray-500 cursor-wait"
                  : "bg-[#0077C0] text-white hover:bg-[#005B90]"
              }`}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <ArrowDownTrayIcon className="w-4 h-4" />
              )}
              {loading ? "Exporting..." : "Generate Report"}
              <ChevronDownIcon className="w-4 h-4 ml-1 opacity-70" />
            </MenuButton>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems className="absolute right-0 bottom-full mb-2 w-44 origin-bottom-right rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-100">
                <div className="p-1.5 space-y-1">
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={() => handleExport("xlsx")}
                        className={`flex items-center w-full px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                          active ? "bg-blue-50 text-blue-700" : "text-gray-700"
                        }`}
                      >
                        Excel Format (.xlsx)
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={() => handleExport("csv")}
                        className={`flex items-center w-full px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                          active ? "bg-blue-50 text-blue-700" : "text-gray-700"
                        }`}
                      >
                        CSV Format (.csv)
                      </button>
                    )}
                  </MenuItem>
                </div>
              </MenuItems>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  );
};

export default LossReportModal;
